/* eslint-disable max-len */
import * as React from 'react';
import type { SVGProps } from 'react';

const baseAnimationProps: React.SVGProps<SVGElement> = {
  attributeName: 'transform',
  type: 'translate',
  repeatCount: 'indefinite',
};

const animations: Record<string, React.SVGProps<SVGElement>> = {
  moveUp: {
    values: '0,0; 0,-30; 0,0',
    keyTimes: '0; 0.5; 1',
  },
  moveDown: {
    values: '0,0; 0,30; 0,0',
    keyTimes: '0; 0.5; 1',
  },
  moveUpAndDown: {
    values: '0,0; 0,-20; 0,10; 0,0',
    keyTimes: '0; 0.3; 0.7; 1',
  },
  subtle: {
    values: '0,0; 0,-3; 0,2; 0,0',
    keyTimes: '0; 0.3; 0.7; 1',
  },
};

type Duration = '15s' | '25s';

const ParticlesBg: React.FunctionComponent<SVGProps<SVGSVGElement>> = (props) => {
  const { className } = props;

  const renderAnimation = React.useCallback(
    (animation: React.SVGProps<SVGElement>, duration: Duration): React.ReactNode => {
      return (
        <animateTransform
          attributeName={baseAnimationProps.attributeName}
          type={baseAnimationProps.type}
          repeatCount={baseAnimationProps.repeatCount}
          values={animation.values}
          keyTimes={animation.keyTimes}
          dur={duration}
        />
      );
    },
    []
  );

  return (
    <svg viewBox="0 0 1052 270" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M525.226 249.197C260.489 248.516 45.883 224.591 45.9572 195.753C46.0313 166.915 260.757 144.093 525.495 144.773C790.232 145.454 1004.84 169.38 1004.76 198.218C1004.69 227.055 789.964 249.877 525.226 249.197Z"
        fill="url(#paint0_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M642.202 201.966C642.233 190.146 653.411 180.559 667.121 180.594C680.831 180.63 691.96 190.274 691.929 202.094C691.899 213.914 680.72 223.501 667.011 223.466C653.301 223.431 642.172 213.786 642.202 201.966Z"
        fill="url(#paint1_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M722.936 206.281C722.966 194.461 734.145 184.873 747.854 184.909C761.564 184.944 772.693 194.589 772.663 206.409C772.632 218.228 761.454 227.816 747.744 227.781C734.034 227.745 722.905 218.101 722.936 206.281Z"
        fill="url(#paint2_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M825.905 22.0112C825.935 10.1914 837.113 0.603968 850.823 0.63921C864.533 0.674453 875.662 10.3192 875.631 22.139C875.601 33.9588 864.423 43.5463 850.713 43.511C837.003 43.4758 825.874 33.831 825.905 22.0112Z"
        fill="url(#paint3_radial_6536_32044)"
      >
        {renderAnimation(animations.moveDown, '25s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M129.94 200.65C129.97 188.83 141.149 179.243 154.858 179.278C168.568 179.313 179.697 188.958 179.667 200.778C179.636 212.597 168.458 222.185 154.748 222.15C141.038 222.114 129.909 212.47 129.94 200.65Z"
        fill="url(#paint4_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M824.585 211.15C824.616 199.33 835.794 189.743 849.504 189.778C863.214 189.813 874.342 199.458 874.312 211.278C874.282 223.098 863.103 232.685 849.394 232.65C835.684 232.614 824.555 222.97 824.585 211.15Z"
        fill="url(#paint5_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M419.711 199.192C419.742 187.372 430.92 177.785 444.63 177.82C458.34 177.855 469.468 187.5 469.438 199.32C469.408 211.139 458.229 220.727 444.52 220.692C430.81 220.656 419.681 211.012 419.711 199.192Z"
        fill="url(#paint6_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        opacity={0.8}
        d="M952.46 222.985C952.491 211.165 963.669 201.578 977.379 201.613C991.089 201.648 1002.22 211.293 1002.19 223.113C1002.16 234.932 990.978 244.52 977.269 244.485C963.559 244.449 952.43 234.805 952.46 222.985Z"
        fill="url(#paint7_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M31.4016 248.172C31.432 236.353 42.6104 226.765 56.3201 226.8C70.0299 226.836 81.1589 236.48 81.1285 248.3C81.0981 260.12 69.9197 269.707 56.2099 269.672C42.5002 269.637 31.3712 259.992 31.4016 248.172Z"
        fill="url(#paint8_radial_6536_32044)"
      >
        {renderAnimation(animations.subtle, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M541.342 205.815C541.372 193.995 552.551 184.408 566.261 184.443C579.97 184.478 591.099 194.123 591.069 205.943C591.039 217.763 579.86 227.35 566.15 227.315C552.441 227.279 541.312 217.635 541.342 205.815Z"
        fill="url(#paint9_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M228.966 188.383C228.996 176.563 240.175 166.976 253.885 167.011C267.594 167.047 278.723 176.691 278.693 188.511C278.663 200.331 267.484 209.918 253.774 209.883C240.065 209.848 228.936 200.203 228.966 188.383Z"
        fill="url(#paint10_radial_6536_32044)"
      >
        {renderAnimation(animations.subtle, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M154.784 208.225C154.815 196.405 165.993 186.818 179.703 186.853C193.413 186.888 204.542 196.533 204.511 208.353C204.481 220.173 193.303 229.76 179.593 229.725C165.883 229.69 154.754 220.045 154.784 208.225Z"
        fill="url(#paint11_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M520.663 205.86C520.691 195.041 530.938 186.253 543.486 186.285C556.034 186.317 566.235 195.159 566.207 205.977C566.18 216.795 555.933 225.583 543.385 225.551C530.837 225.519 520.635 216.678 520.663 205.86Z"
        fill="url(#paint12_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M502.486 225.548C502.499 220.74 507.04 216.946 512.5 216.96C517.961 216.974 522.482 220.892 522.47 225.6C522.458 230.308 517.917 234.203 512.456 234.189C506.995 234.174 502.474 230.256 502.486 225.548Z"
        fill="url(#paint13_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M641.488 206.171C641.493 204.368 643.24 202.87 645.331 202.875C647.422 202.88 649.161 204.387 649.157 206.19C649.152 207.993 647.405 209.491 645.314 209.486C643.223 209.481 641.484 207.974 641.488 206.171Z"
        fill="url(#paint14_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M589.217 202.832C589.222 201.029 590.852 199.631 592.943 199.636C595.035 199.641 596.658 201.048 596.653 202.851C596.648 204.654 595.018 206.052 592.927 206.047C590.835 206.041 589.212 204.635 589.217 202.832Z"
        fill="url(#paint15_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M300.75 135.696C300.755 133.893 302.385 132.495 304.476 132.5C306.568 132.506 308.191 133.912 308.186 135.715C308.181 137.518 306.551 138.916 304.46 138.911C302.369 138.906 300.746 137.499 300.75 135.696Z"
        fill="url(#paint16_radial_6536_32044)"
      >
        {renderAnimation(animations.subtle, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M813.003 93.5532C813.012 90.0473 816.272 87.1508 820.455 87.1615C824.637 87.1723 827.883 89.9854 827.874 93.5914C827.865 97.1974 824.605 99.9938 820.422 99.983C816.239 99.9723 812.993 97.1592 813.003 93.5532Z"
        fill="url(#paint17_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUpAndDown, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M345.816 139.017C345.825 135.511 349.086 132.615 353.268 132.625C357.451 132.636 360.697 135.449 360.688 139.055C360.678 142.661 357.418 145.458 353.235 145.447C349.053 145.436 345.807 142.623 345.816 139.017Z"
        fill="url(#paint18_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '25s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M254.517 110.354C250.41 110.344 247.088 107.465 247.097 103.924C247.106 100.384 250.443 97.5221 254.55 97.5326C258.656 97.5432 261.978 100.422 261.969 103.962C261.96 107.503 258.623 110.365 254.517 110.354Z"
        fill="url(#paint19_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUpAndDown, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M157.327 169.868C157.336 166.362 160.596 163.465 164.779 163.476C168.962 163.487 172.208 166.3 172.198 169.906C172.189 173.512 168.929 176.308 164.746 176.297C160.563 176.287 157.318 173.474 157.327 169.868Z"
        fill="url(#paint20_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '25s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M910.068 169.868C910.077 166.362 913.338 163.465 917.52 163.476C921.703 163.487 924.949 166.3 924.94 169.906C924.93 173.512 921.67 176.308 917.487 176.297C913.305 176.287 910.059 173.474 910.068 169.868Z"
        fill="url(#paint21_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M316.692 25.026C312.586 25.0154 309.264 22.1367 309.273 18.5961C309.282 15.0556 312.619 12.194 316.725 12.2045C320.832 12.2151 324.154 15.0938 324.145 18.6344C324.136 22.1749 320.799 25.0365 316.692 25.026Z"
        fill="url(#paint22_radial_6536_32044)"
      >
        {renderAnimation(animations.moveDown, '25s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M130.343 181.719C130.352 178.213 133.613 175.317 137.796 175.328C141.978 175.338 145.224 178.151 145.215 181.757C145.206 185.363 141.945 188.16 137.763 188.149C133.58 188.138 130.334 185.325 130.343 181.719Z"
        fill="url(#paint23_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M412.38 205.482C412.39 201.976 415.65 199.08 419.833 199.09C424.015 199.101 427.261 201.914 427.252 205.52C427.243 209.126 423.982 211.922 419.8 211.912C415.617 211.901 412.371 209.088 412.38 205.482Z"
        fill="url(#paint24_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M630.498 189.414C630.507 185.908 633.767 183.011 637.95 183.022C642.133 183.033 645.379 185.846 645.369 189.452C645.36 193.058 642.1 195.854 637.917 195.843C633.734 195.833 630.488 193.02 630.498 189.414Z"
        fill="url(#paint25_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M553.694 192.723C549.588 192.713 546.266 189.834 546.275 186.293C546.284 182.753 549.621 179.891 553.727 179.902C557.834 179.912 561.156 182.791 561.147 186.332C561.138 189.872 557.801 192.734 553.694 192.723Z"
        fill="url(#paint26_radial_6536_32044)"
      />
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M818.421 168.362C818.426 166.559 820.056 165.161 822.147 165.166C824.239 165.172 825.862 166.578 825.857 168.381C825.852 170.184 824.222 171.582 822.131 171.577C820.04 171.572 818.417 170.165 818.421 168.362Z"
        fill="url(#paint27_radial_6536_32044)"
      >
        {renderAnimation(animations.subtle, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M768.829 139.664C768.834 137.861 770.464 136.463 772.556 136.468C774.647 136.473 776.27 137.88 776.265 139.683C776.261 141.486 774.63 142.884 772.539 142.879C770.448 142.873 768.825 141.467 768.829 139.664Z"
        fill="url(#paint28_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M349.359 73.2713C347.306 73.266 345.645 71.8266 345.65 70.0564C345.654 68.2861 347.323 66.8553 349.376 66.8606C351.429 66.8658 353.09 68.3052 353.086 70.0755C353.081 71.8458 351.413 73.2766 349.359 73.2713Z"
        fill="url(#paint29_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M126.46 63.5106C124.407 63.5053 122.746 62.0659 122.75 60.2956C122.755 58.5253 124.423 57.0945 126.476 57.0998C128.53 57.1051 130.191 58.5445 130.186 60.3147C130.182 62.085 128.513 63.5158 126.46 63.5106Z"
        fill="url(#paint30_radial_6536_32044)"
      >
        {renderAnimation(animations.subtle, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M105.844 130.471C103.79 130.465 102.13 129.026 102.134 127.256C102.139 125.485 103.807 124.055 105.86 124.06C107.914 124.065 109.574 125.504 109.57 127.275C109.565 129.045 107.897 130.476 105.844 130.471Z"
        fill="url(#paint31_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUp, '15s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M923.902 104.162C923.911 100.656 927.172 97.7592 931.354 97.7699C935.537 97.7807 938.783 100.594 938.774 104.2C938.764 107.806 935.504 110.602 931.321 110.591C927.139 110.581 923.893 107.768 923.902 104.162Z"
        fill="url(#paint32_radial_6536_32044)"
      >
        {renderAnimation(animations.moveUpAndDown, '25s')}
      </path>
      <path
        style={{
          mixBlendMode: 'screen',
        }}
        d="M908.019 206.856C905.965 206.851 904.304 205.412 904.309 203.641C904.313 201.871 905.982 200.44 908.035 200.446C910.088 200.451 911.749 201.89 911.745 203.66C911.74 205.431 910.072 206.862 908.019 206.856Z"
        fill="url(#paint33_radial_6536_32044)"
      />
      <defs>
        <radialGradient
          id="paint0_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(525.327 192.282) rotate(90.1473) scale(52.2908 478.753)"
        >
          <stop stopColor="#FFD996" />
          <stop offset={0.05} stopColor="#F1C987" />
          <stop offset={0.14} stopColor="#CDA061" />
          <stop offset={0.27} stopColor="#945F23" />
          <stop offset={0.28} stopColor="#915B20" />
          <stop offset={0.4} stopColor="#653F16" />
          <stop offset={0.52} stopColor="#40280E" />
          <stop offset={0.64} stopColor="#241608" />
          <stop offset={0.76} stopColor="#100A03" />
          <stop offset={0.88} stopColor="#040200" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint1_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(667.066 202.03) rotate(-179.853) scale(24.8635 21.436)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint2_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(747.915 206.345) rotate(-179.853) scale(5.80924 5.00841)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint3_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(850.768 22.0751) rotate(-179.853) scale(5.80923 5.0084)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint4_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(155.277 16.4051) rotate(-179.853) scale(5.80923 5.00841)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint5_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(849.449 211.214) rotate(-179.853) scale(5.80923 5.00841)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint6_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(444.691 199.256) rotate(-179.853) scale(5.80924 5.0084)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint7_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(977.324 223.049) rotate(-179.853) scale(24.8635 21.436)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint8_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(56.265 248.236) rotate(-179.853) scale(24.8635 21.436)"
        >
          <stop stopColor="#FFA94D" />
          <stop offset={0.03} stopColor="#E49745" />
          <stop offset={0.1} stopColor="#B57836" />
          <stop offset={0.17} stopColor="#895B29" />
          <stop offset={0.24} stopColor="#64421E" />
          <stop offset={0.32} stopColor="#452D14" />
          <stop offset={0.41} stopColor="#2B1D0D" />
          <stop offset={0.51} stopColor="#181007" />
          <stop offset={0.62} stopColor="#0A0603" />
          <stop offset={0.75} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint9_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(566.322 205.879) rotate(-179.853) scale(5.80923 5.00841)"
        >
          <stop stopColor="#FFA94D" />
          <stop offset={0.03} stopColor="#E49745" />
          <stop offset={0.1} stopColor="#B57836" />
          <stop offset={0.17} stopColor="#895B29" />
          <stop offset={0.24} stopColor="#64421E" />
          <stop offset={0.32} stopColor="#452D14" />
          <stop offset={0.41} stopColor="#2B1D0D" />
          <stop offset={0.51} stopColor="#181007" />
          <stop offset={0.62} stopColor="#0A0603" />
          <stop offset={0.75} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint10_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(253.83 188.447) rotate(-179.853) scale(24.8635 21.436)"
        >
          <stop stopColor="#FFA94D" />
          <stop offset={0.03} stopColor="#E49745" />
          <stop offset={0.1} stopColor="#B57836" />
          <stop offset={0.17} stopColor="#895B29" />
          <stop offset={0.24} stopColor="#64421E" />
          <stop offset={0.32} stopColor="#452D14" />
          <stop offset={0.41} stopColor="#2B1D0D" />
          <stop offset={0.51} stopColor="#181007" />
          <stop offset={0.62} stopColor="#0A0603" />
          <stop offset={0.75} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint11_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(179.648 208.289) rotate(-179.853) scale(24.8635 21.436)"
        >
          <stop stopColor="#FFA94D" />
          <stop offset={0.03} stopColor="#E49745" />
          <stop offset={0.1} stopColor="#B57836" />
          <stop offset={0.17} stopColor="#895B29" />
          <stop offset={0.24} stopColor="#64421E" />
          <stop offset={0.32} stopColor="#452D14" />
          <stop offset={0.41} stopColor="#2B1D0D" />
          <stop offset={0.51} stopColor="#181007" />
          <stop offset={0.62} stopColor="#0A0603" />
          <stop offset={0.75} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint12_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(543.435 205.918) rotate(-179.853) scale(22.7722 19.6329)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint13_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(512.594 225.574) rotate(-179.853) scale(9.99187 8.61445)"
        >
          <stop stopColor="#FFE07B" />
          <stop offset={0.04} stopColor="#FFBB63" />
          <stop offset={0.07} stopColor="#FFA053" />
          <stop offset={0.09} stopColor="#FF974D" />
          <stop offset={0.12} stopColor="#E48745" />
          <stop offset={0.17} stopColor="#B56B36" />
          <stop offset={0.24} stopColor="#895129" />
          <stop offset={0.31} stopColor="#643B1E" />
          <stop offset={0.38} stopColor="#452914" />
          <stop offset={0.46} stopColor="#2B190D" />
          <stop offset={0.55} stopColor="#180E07" />
          <stop offset={0.65} stopColor="#0A0603" />
          <stop offset={0.78} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint14_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(645.206 206.18) rotate(-179.853) scale(3.83409 3.30555)"
        >
          <stop stopColor="#FFE07B" />
          <stop offset={0.04} stopColor="#FFBB63" />
          <stop offset={0.07} stopColor="#FFA053" />
          <stop offset={0.09} stopColor="#FF974D" />
          <stop offset={0.12} stopColor="#E48745" />
          <stop offset={0.17} stopColor="#B56B36" />
          <stop offset={0.24} stopColor="#895129" />
          <stop offset={0.31} stopColor="#643B1E" />
          <stop offset={0.38} stopColor="#452914" />
          <stop offset={0.46} stopColor="#2B190D" />
          <stop offset={0.55} stopColor="#180E07" />
          <stop offset={0.65} stopColor="#0A0603" />
          <stop offset={0.78} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint15_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(592.819 202.841) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint16_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(304.468 135.706) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint17_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(820.322 93.572) rotate(-179.853) scale(7.43583 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint18_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(353.136 139.036) rotate(-179.853) scale(7.43583 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint19_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(254.533 103.943) rotate(-179.853) scale(7.43583 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint20_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(164.763 169.887) rotate(-179.853) scale(7.43581 6.41075)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint21_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(917.504 169.887) rotate(-179.853) scale(7.43581 6.41075)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint22_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(316.709 18.6153) rotate(-179.853) scale(7.43581 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint23_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(137.779 181.738) rotate(-179.853) scale(7.43581 6.41075)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint24_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(419.7 205.501) rotate(-179.853) scale(7.43581 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint25_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(637.933 189.433) rotate(-179.853) scale(7.43581 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint26_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(553.711 186.313) rotate(-179.853) scale(7.43581 6.41076)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint27_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(822.023 168.371) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint28_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(772.547 139.673) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint29_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(349.368 70.0659) rotate(-179.853) scale(3.71793 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint30_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(126.468 60.3052) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint31_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(105.852 127.265) rotate(-179.853) scale(3.71791 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint32_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(931.338 104.181) rotate(-179.853) scale(7.43581 6.41075)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
        <radialGradient
          id="paint33_radial_6536_32044"
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(908.027 203.651) rotate(-179.853) scale(3.71792 3.20538)"
        >
          <stop stopColor="#FFFAF2" />
          <stop offset={0.09} stopColor="#FFE07B" />
          <stop offset={0.11} stopColor="#FFCD6B" />
          <stop offset={0.14} stopColor="#FFB255" />
          <stop offset={0.16} stopColor="#FFA94D" />
          <stop offset={0.18} stopColor="#E49745" />
          <stop offset={0.24} stopColor="#B57836" />
          <stop offset={0.3} stopColor="#895B29" />
          <stop offset={0.36} stopColor="#64421E" />
          <stop offset={0.43} stopColor="#452D14" />
          <stop offset={0.5} stopColor="#2B1D0D" />
          <stop offset={0.58} stopColor="#181007" />
          <stop offset={0.68} stopColor="#0A0603" />
          <stop offset={0.79} stopColor="#020100" />
          <stop offset={1} />
        </radialGradient>
      </defs>
    </svg>
  );
};

export default ParticlesBg;
/* eslint-enable max-len */
